'use client'

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { observer } from 'mobx-react-lite'
import {
  X,
  ArrowLeft,
  HelpCircle,
  LifeBuoy,
  Bug,
  Lightbulb,
  Paperclip,
  Film,
  FileIcon,
  Search,
  MessageCircle,
} from 'lucide-react'
import {
  ConversationsRepository,
  SupportSubCategory,
  MessageAttachment,
  HelpOrderPickerItem,
  HelpListingPickerItem,
  HelpPayoutPickerItem,
} from '@/core/repositories/conversations'
import { AppStore } from '@/core/store'
import { CopyIdButton } from '@/components/common/copy-id-button'
import { orderLabel } from '@/core/utils/order-number'
import { collectDebugBundle } from '@/lib/debug-bundle'

/**
 * Two-step intake:
 *
 *   Step 1 — Tile picker (Question / Need help / Bug / Idea)
 *   Step 2 — Per-tile composer with the right fields for that intent
 *
 * Why tiles: a help-intake screen is a "moment" — users are stopping their
 * task to ask for help. A grid of icon tiles is faster to read, more inviting,
 * and signals each option's distinct purpose at a glance. Dropdowns are
 * utilitarian; tiles set the right tone.
 *
 * Tile → backend mapping:
 *   Question     → category=inquiry,    priority=low
 *   Need help    → category=inquiry,    priority=high  + sub-category dropdown
 *   Bug          → category=bug_report, priority=normal + debug bundle attached
 *   Idea         → category=suggestion, priority=low
 *
 * Lane is auto-detected from the page path the user came from (sell/seller
 * surfaces → seller, otherwise buyer). Never asked.
 */

/**
 * Trailing-debounce a string value. Used by the picker search inputs to
 * coalesce keystrokes into a single backend hit ~300ms after the user pauses.
 */
function useDebouncedValue<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(t)
  }, [value, delayMs])
  return debounced
}

type Tile = 'question' | 'help' | 'bug' | 'idea'

interface TileMeta {
  key: Tile
  label: string
  hint: string
  icon: React.ReactNode
  accent: string // CSS color for the icon
}

const TILES: TileMeta[] = [
  {
    key: 'question',
    label: 'Contact support',
    hint: 'How something works, where to find a feature',
    icon: <HelpCircle size={22} strokeWidth={1.75} />,
    accent: 'var(--ios-blue)',
  },
  {
    key: 'help',
    label: 'Need help?',
    hint: 'Account, order, payment, payout',
    icon: <LifeBuoy size={22} strokeWidth={1.75} />,
    accent: 'var(--ios-orange)',
  },
  {
    key: 'bug',
    label: 'Found a bug?',
    hint: "Something's broken or not working right",
    icon: <Bug size={22} strokeWidth={1.75} />,
    accent: 'var(--ios-red)',
  },
  {
    key: 'idea',
    label: 'Got an idea?',
    hint: 'Suggest a feature or improvement',
    icon: <Lightbulb size={22} strokeWidth={1.75} />,
    accent: 'var(--ios-green)',
  },
]

/**
 * Sub-category labels are written so the user can self-disambiguate without
 * needing to know about lanes / roles. Role-restricted options ('payout',
 * 'listing', and the seller-flavored 'order' label "Item I sold") are only
 * surfaced to users who actually have a seller profile.
 */
const SUB_CATEGORY_LABEL: Record<SupportSubCategory, string> = {
  order: 'Order I bought',
  // Seller-only options
  listing: 'Listing',
  payout: 'Payout',
  account: 'Account',
  other: 'Other',
}

// Buyer-side options every user sees
const BUYER_SUBCATS: SupportSubCategory[] = ['order']
// Seller-side options only shown to users with a seller profile
const SELLER_SUBCATS: SupportSubCategory[] = ['listing', 'payout']
// Generic options every user sees
const GENERAL_SUBCATS: SupportSubCategory[] = ['account', 'other']

export const CategoryPicker = observer(({
  onClose,
  onCreated,
  initialTile,
}: {
  onClose: () => void
  onCreated: (id: string) => void
  /** Preselect a tile so the modal opens straight into the composer. Used by
   *  /inbox?compose=<tile> deep-links (e.g. the hamburger menu's "Contact Us"
   *  entry, which is now a question-tile shortcut). */
  initialTile?: Tile
}) => {
  // isSeller drives the seller-only sub-category options. Lane on the
  // conversation is determined server-side from the user's actual role
  // (this client value races auth bootstrap and isn't authoritative).
  const isSeller = AppStore.sellerStatus.isSeller
  const [tile, setTile] = useState<Tile | null>(initialTile ?? null)

  // When the picker is opened while viewing a listing, offer a direct line to
  // that listing's seller (separate from MarkitMix support). Hidden on the
  // viewer's own listing and when logged out. Reads route-scoped state the
  // listing page publishes to AppStore.
  const activeListing = AppStore.activeListing
  const viewerId = AppStore.accountData?.id
  const canMessageSeller =
    !initialTile && !!activeListing && !!viewerId && activeListing.sellerId !== viewerId

  const openSellerChat = () => {
    if (!activeListing) return
    // Hand off to the floating help panel, which opens the listing chat in
    // place (existing thread or a draft composer). Nothing is created until
    // the buyer sends. Close the picker so the panel takes over.
    window.dispatchEvent(
      new CustomEvent('mm-open-listing-chat', { detail: { listingId: activeListing.id } })
    )
    onClose()
  }

  // Lock background scroll while modal is open (existing project convention)
  useEffect(() => {
    document.body.classList.add('no-scroll')
    return () => document.body.classList.remove('no-scroll')
  }, [])

  // Portal to <body> so we escape any ancestor that creates a stacking /
  // containing block (the inbox sidebar uses backdrop-filter for its
  // translucent macOS look — that traps `position: fixed` inside the
  // sidebar without this).
  if (typeof document === 'undefined') return null
  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.45)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        zIndex: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--background_1, var(--background_2))',
          borderRadius: 18,
          maxWidth: 460,
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.25)',
          padding: 22,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {tile && (
            <button
              onClick={() => setTile(null)}
              aria-label="Back"
              style={iconButtonStyle}
            >
              <ArrowLeft size={18} />
            </button>
          )}
          <h2
            style={{
              margin: 0,
              flex: 1,
              fontSize: 20,
              fontWeight: 600,
              color: 'var(--font_1)',
              letterSpacing: '-0.01em',
            }}
          >
            {tile
              ? TILES.find((t) => t.key === tile)?.label
              : 'How can we help?'}
          </h2>
          <button onClick={onClose} aria-label="Close" style={iconButtonStyle}>
            <X size={18} />
          </button>
        </div>

        {!tile ? (
          <>
            {canMessageSeller && (
              <>
                <button
                  type="button"
                  onClick={openSellerChat}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    width: '100%',
                    padding: '14px 16px',
                    borderRadius: 14,
                    border: '1px solid color-mix(in srgb, var(--primary) 35%, var(--separator))',
                    background: 'color-mix(in srgb, var(--primary) 6%, var(--background_2))',
                    color: 'var(--font_1)',
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                >
                  <span
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 10,
                      background: 'color-mix(in srgb, var(--primary) 14%, transparent)',
                      color: 'var(--primary)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <MessageCircle size={20} strokeWidth={1.9} />
                  </span>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontWeight: 600, fontSize: 14, letterSpacing: '-0.01em' }}>
                      Message the seller of this item
                    </span>
                    <span style={{ display: 'block', fontSize: 12, color: 'var(--font_3)', lineHeight: 1.4 }}>
                      Ask the seller a question about this listing
                    </span>
                  </span>
                </button>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: 'var(--font_3)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    marginTop: 4,
                  }}
                >
                  Or contact the MarkitMix team
                </div>
              </>
            )}
            {!canMessageSeller && (
              <p
                style={{
                  margin: 0,
                  fontSize: 14,
                  color: 'var(--font_2)',
                  lineHeight: 1.45,
                }}
              >
                We&apos;ll get back to you as soon as we can. Pick what fits best.
              </p>
            )}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: 10,
              }}
            >
              {TILES.map((t) => (
                <TileButton key={t.key} meta={t} onClick={() => setTile(t.key)} />
              ))}
            </div>
          </>
        ) : (
          // Keying on `tile` remounts TileComposer whenever the user changes
          // tiles, so all per-tile state (draft text, attachments, topic
          // selections, error) is scoped to a single tile's lifecycle and
          // can never leak into another. React's recommended pattern for
          // "reset all state when a prop changes."
          <TileComposer
            key={tile}
            tile={tile}
            isSeller={isSeller}
            onCreated={onCreated}
          />
        )}
      </div>
    </div>,
    document.body
  )
})

/**
 * Owns all per-tile composer state — draft text, attachments, topic + entity
 * selections, error. Mounted with `key={tile}` so switching tiles destroys
 * this component and creates a fresh one, guaranteeing no state crosses
 * tiles.
 */
const TileComposer = ({
  tile,
  isSeller,
  onCreated,
}: {
  tile: Tile
  isSeller: boolean
  onCreated: (id: string) => void
}) => {
  const [subCategory, setSubCategory] = useState<SupportSubCategory | null>(null)
  const [selectedOrder, setSelectedOrder] = useState<HelpOrderPickerItem | null>(null)
  const [selectedListing, setSelectedListing] = useState<HelpListingPickerItem | null>(null)
  const [selectedPayout, setSelectedPayout] = useState<HelpPayoutPickerItem | null>(null)
  const [body, setBody] = useState('')
  const [bugWhatHappened, setBugWhatHappened] = useState('')
  const [attachments, setAttachments] = useState<MessageAttachment[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isBug = tile === 'bug'

  const placeholder = (() => {
    switch (tile) {
      case 'question':
        return "What's your question?"
      case 'help':
        return 'Tell us what happened. The more detail, the faster we can help.'
      case 'bug':
        return 'What were you trying to do?'
      case 'idea':
        return 'What would you like to see?'
    }
  })()

  const hasText =
    body.trim().length > 0 || (isBug && bugWhatHappened.trim().length > 0)
  // For Need help, force a topic pick before allowing submit. Other tiles
  // don't have a sub-category so any text/attachment is enough.
  const helpReady = tile !== 'help' || subCategory !== null
  const canSubmit = (hasText || attachments.length > 0) && helpReady

  const submit = async () => {
    if (!canSubmit) return
    setSubmitting(true)
    setError(null)
    try {
      const fullBody = isBug
        ? body.trim() || bugWhatHappened.trim()
          ? `What I was doing:\n${body.trim()}\n\nWhat happened:\n${bugWhatHappened.trim()}`
          : ''
        : body.trim()

      const conv = await ConversationsRepository.createSupport({
        category:
          tile === 'bug'
            ? 'bug_report'
            : tile === 'idea'
            ? 'suggestion'
            : 'inquiry',
        priority:
          tile === 'help' ? 'high' : tile === 'bug' ? 'normal' : 'low',
        body: fullBody || undefined,
        // Lane is determined server-side from the user's role + sub-category;
        // sending a hint from here would race against AppStore.sellerStatus
        // load (it used to misroute seller bug reports to buyer lane).
        subCategory: tile === 'help' && subCategory ? subCategory : undefined,
        // When the user picked a specific order, the backend routes the message
        // to a peer conversation with the seller of that order.
        orderId:
          tile === 'help' && subCategory === 'order' && selectedOrder
            ? selectedOrder.id
            : undefined,
        // Seller-side: listing / payout IDs are stashed in metadata so ops
        // can pull the entity context in /console/support with one click.
        listingId:
          tile === 'help' && subCategory === 'listing' && selectedListing
            ? selectedListing.id
            : undefined,
        payoutId:
          tile === 'help' && subCategory === 'payout' && selectedPayout
            ? selectedPayout.id
            : undefined,
        attachments: attachments.length > 0 ? attachments : undefined,
        metadata:
          tile === 'bug'
            ? { debugBundle: await collectDebugBundle() }
            : undefined,
      })
      onCreated(conv.id)
    } catch (err: any) {
      setError(err.message || 'Failed to send')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      {tile === 'help' && (
        <Field label="What's it about?">
          <select
            value={subCategory ?? ''}
            onChange={(e) => {
              const v = e.target.value
              setSubCategory(v ? (v as SupportSubCategory) : null)
              setSelectedOrder(null)
              setSelectedListing(null)
              setSelectedPayout(null)
            }}
            style={{
              ...selectStyle,
              color: subCategory ? 'var(--font_1)' : 'var(--font_3)',
            }}
          >
            <option value="" disabled>
              Choose a topic…
            </option>
            <optgroup label="Buying">
              {BUYER_SUBCATS.map((s) => (
                <option key={s} value={s}>
                  {SUB_CATEGORY_LABEL[s]}
                </option>
              ))}
            </optgroup>
            {isSeller && (
              <optgroup label="Selling">
                {SELLER_SUBCATS.map((s) => (
                  <option key={s} value={s}>
                    {SUB_CATEGORY_LABEL[s]}
                  </option>
                ))}
              </optgroup>
            )}
            <optgroup label="General">
              {GENERAL_SUBCATS.map((s) => (
                <option key={s} value={s}>
                  {SUB_CATEGORY_LABEL[s]}
                </option>
              ))}
            </optgroup>
          </select>
        </Field>
      )}

      {tile === 'help' && subCategory === 'order' && (
        <OrderPicker selected={selectedOrder} onSelect={setSelectedOrder} />
      )}

      {tile === 'help' && subCategory === 'listing' && (
        <ListingPicker selected={selectedListing} onSelect={setSelectedListing} />
      )}

      {tile === 'help' && subCategory === 'payout' && (
        <PayoutPicker selected={selectedPayout} onSelect={setSelectedPayout} />
      )}

      <Field label={isBug ? 'What were you doing?' : 'Tell us more'}>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={isBug ? 3 : 5}
          placeholder={placeholder}
          style={textareaStyle}
          autoFocus
        />
        {!isBug && (
          <FileAttacher
            attachments={attachments}
            setAttachments={setAttachments}
            totalCount={attachments.length}
          />
        )}
      </Field>

      {isBug && (
        <>
          <Field label="What happened instead?">
            <textarea
              value={bugWhatHappened}
              onChange={(e) => setBugWhatHappened(e.target.value)}
              rows={3}
              placeholder="What you saw vs. what you expected"
              style={textareaStyle}
            />
            <FileAttacher
              attachments={attachments}
              setAttachments={setAttachments}
              totalCount={attachments.length}
            />
          </Field>
          <DebugBundleNote />
        </>
      )}

      {error && (
        <div
          style={{
            padding: 10,
            background: 'color-mix(in srgb, var(--ios-red) 10%, transparent)',
            border: '1px solid color-mix(in srgb, var(--ios-red) 28%, var(--separator))',
            borderRadius: 10,
            color: 'var(--font_1)',
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      <button
        onClick={submit}
        disabled={!canSubmit || submitting}
        style={{
          background: 'var(--primary)',
          color: 'var(--primary_text)',
          border: 'none',
          fontSize: 15,
          fontWeight: 500,
          padding: '0 18px',
          height: 44,
          borderRadius: 12,
          cursor: canSubmit && !submitting ? 'pointer' : 'not-allowed',
          opacity: canSubmit && !submitting ? 1 : 0.5,
          marginTop: 4,
          letterSpacing: '-0.01em',
        }}
      >
        {submitting ? 'Sending…' : 'Send'}
      </button>
    </>
  )
}

// ── Sub-components ─────────────────────────────────────────────────────────

const TileButton = ({
  meta,
  onClick,
}: {
  meta: TileMeta
  onClick: () => void
}) => {
  const [hover, setHover] = useState(false)
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 8,
        padding: '16px 14px',
        borderRadius: 14,
        border: `1px solid ${hover ? 'color-mix(in srgb, var(--primary) 35%, var(--separator))' : 'var(--separator)'}`,
        background: hover
          ? 'color-mix(in srgb, var(--primary) 4%, var(--background_2))'
          : 'var(--background_2)',
        color: 'var(--font_1)',
        fontSize: 14,
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'transform 120ms ease, box-shadow 120ms ease, background 120ms, border-color 120ms',
        transform: hover ? 'translateY(-1px)' : 'translateY(0)',
        boxShadow: hover
          ? '0 6px 16px rgba(0, 0, 0, 0.08)'
          : '0 1px 2px rgba(0, 0, 0, 0.03)',
        minHeight: 116,
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          background: `color-mix(in srgb, ${meta.accent} 12%, transparent)`,
          color: meta.accent,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {meta.icon}
      </div>
      <span
        style={{
          fontWeight: 600,
          fontSize: 14,
          letterSpacing: '-0.01em',
        }}
      >
        {meta.label}
      </span>
      <span
        style={{
          fontSize: 12,
          color: 'var(--font_3)',
          lineHeight: 1.4,
        }}
      >
        {meta.hint}
      </span>
    </button>
  )
}

// ── OrderPicker — for "Need help → Order I bought" ──────────────────────

const OrderPicker = ({
  selected,
  onSelect,
}: {
  selected: HelpOrderPickerItem | null
  onSelect: (o: HelpOrderPickerItem | null) => void
}) => {
  const [orders, setOrders] = useState<HelpOrderPickerItem[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebouncedValue(query, 300)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await ConversationsRepository.getRecentOrders(
          debouncedQuery || undefined
        )
        if (!cancelled) setOrders(res)
      } catch {
        if (!cancelled) setOrders([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [debouncedQuery])

  // Picked-state — selected order summary with change button
  if (selected) {
    return (
      <Field label="Which order?">
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            padding: 12,
            borderRadius: 12,
            border: '1px solid var(--separator)',
            background: 'color-mix(in srgb, var(--primary) 5%, var(--background_2))',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12,
            }}
          >
            <OrderThumb path={selected.thumbnailPath} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: 'var(--font_1)',
                  letterSpacing: '-0.01em',
                  lineHeight: 1.3,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical' as const,
                  overflow: 'hidden',
                  wordBreak: 'break-word',
                }}
              >
                {selected.title}
              </div>
              <OrderMeta order={selected} />
            </div>
            <button
              type="button"
              onClick={() => onSelect(null)}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--primary)',
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
                padding: '4px 8px',
                flexShrink: 0,
              }}
            >
              Change
            </button>
          </div>
          <OrderNumberLine orderNumber={selected.orderNumber} />
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            marginTop: 6,
            paddingLeft: 2,
          }}
        >
          <span style={{ fontSize: 12, color: 'var(--font_2)' }}>
            Your message will go to <strong>{selected.seller.name}</strong>, the
            seller of this order.
          </span>
          {selected.orderNumber && <CopyIdButton value={selected.orderNumber} label="Copy" />}
        </div>
      </Field>
    )
  }

  const hasQuery = debouncedQuery.trim().length > 0
  return (
    <Field label="Which order? (optional, pick one to message the seller directly)">
      <PickerSearchInput
        value={query}
        onChange={setQuery}
        placeholder="Search by order number, tracking number, or title"
      />
      {loading ? (
        <div style={{ fontSize: 13, color: 'var(--font_3)', padding: 8 }}>
          Loading your recent orders…
        </div>
      ) : orders && orders.length > 0 ? (
        <div
          style={{
            border: '1px solid var(--separator)',
            borderRadius: 12,
            overflow: 'hidden',
          }}
        >
          {orders.map((o, i) => (
            <button
              key={o.id}
              type="button"
              onClick={() => onSelect(o)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                padding: '10px 12px',
                background: 'var(--background_2)',
                border: 'none',
                borderTop: i > 0 ? '1px solid var(--separator)' : 'none',
                cursor: 'pointer',
                textAlign: 'left',
                color: 'var(--font_1)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  width: '100%',
                }}
              >
                <OrderThumb path={o.thumbnailPath} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: 'var(--font_1)',
                      letterSpacing: '-0.01em',
                      lineHeight: 1.3,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical' as const,
                      overflow: 'hidden',
                      wordBreak: 'break-word',
                    }}
                  >
                    {o.title}
                  </div>
                  <OrderMeta order={o} />
                </div>
              </div>
              <OrderNumberLine orderNumber={o.orderNumber} />
            </button>
          ))}
        </div>
      ) : (
        <PickerEmptyState
          message={
            hasQuery
              ? `No orders match "${debouncedQuery.trim()}"`
              : 'No orders yet. You can still send a generic question below.'
          }
        />
      )}
    </Field>
  )
}

/**
 * Sub-line under the order title — seller and date. Pushed to the bottom
 * of the right column (parent uses `justifyContent: space-between` against
 * a stretched height) so when the title is short there's breathing space
 * between the title and the meta instead of everything bunched at the top.
 *
 * Color hierarchy: "from" is muted (var(--font_3)), seller name is more
 * readable (var(--font_2)) so they don't blur together. Date matches seller
 * weight so the row feels like a single piece of metadata.
 */
const OrderMeta = ({ order }: { order: HelpOrderPickerItem }) => (
  <div
    style={{
      display: 'flex',
      flexWrap: 'wrap',
      columnGap: 10,
      rowGap: 2,
      fontSize: 12,
    }}
  >
    <span>
      <span style={{ color: 'var(--font_3)' }}>sold by </span>
      <span style={{ color: 'var(--font_2)', fontWeight: 500 }}>
        {order.seller.name}
      </span>
    </span>
    <span style={{ color: 'var(--font_2)' }}>{fmtOrderDate(order.createdAt)}</span>
  </div>
)

/**
 * Full-width row under the thumb+title block that names an order by its
 * number, wrapping when it is one of the long numbers issued before
 * 2026-09-19. Orders never show their UUID here.
 */
const OrderNumberLine = ({ orderNumber }: { orderNumber: string | null | undefined }) =>
  orderNumber ? (
    <div className="mt-2 border-t border-[var(--separator)] pt-2 text-xs leading-[1.35] text-[var(--font_3)] [overflow-wrap:anywhere]">
      {orderLabel(orderNumber)}
    </div>
  ) : null

/**
 * Full-width ID row that sits below the thumb+title block. Gets the entire
 * row width so a 36-char UUID can either fit on one line (wide viewports)
 * or wrap cleanly without squeezing the rest of the row.
 */
const OrderIdLine = ({ id }: { id: string }) => (
  <div
    style={{
      fontFamily:
        'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
      fontSize: 11,
      color: 'var(--font_3)',
      wordBreak: 'break-all',
      lineHeight: 1.35,
      paddingTop: 8,
      borderTop: '1px solid var(--separator)',
      marginTop: 8,
    }}
  >
    <span style={{ marginRight: 6 }}>ID:</span>
    {id}
  </div>
)

function fmtOrderDate(iso: string): string {
  const d = new Date(iso)
  const now = new Date()
  const opts: Intl.DateTimeFormatOptions =
    d.getFullYear() === now.getFullYear()
      ? { month: 'short', day: 'numeric' }
      : { month: 'short', day: 'numeric', year: 'numeric' }
  return d.toLocaleDateString('en-US', opts)
}

const OrderThumb = ({ path }: { path: string | null }) => {
  const url = path
    ? path.startsWith('http')
      ? path
      : `${process.env.NEXT_PUBLIC_SERVER_URL || ''}/${path.startsWith('assets/') ? path : `assets/${path}`}`
    : null
  return (
    <div
      style={{
        width: 56,
        height: 56,
        borderRadius: 10,
        background: 'var(--background_3, var(--separator))',
        flexShrink: 0,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {url ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={url}
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <FileIcon size={20} style={{ color: 'var(--font_3)' }} />
      )}
    </div>
  )
}

// ── Listing picker (seller-side) ─────────────────────────────────────────

const ListingPicker = ({
  selected,
  onSelect,
}: {
  selected: HelpListingPickerItem | null
  onSelect: (l: HelpListingPickerItem | null) => void
}) => {
  const [listings, setListings] = useState<HelpListingPickerItem[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebouncedValue(query, 300)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await ConversationsRepository.getRecentListings(
          debouncedQuery || undefined
        )
        if (!cancelled) setListings(res)
      } catch {
        if (!cancelled) setListings([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [debouncedQuery])

  if (selected) {
    return (
      <Field label="Which listing?">
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            padding: 12,
            borderRadius: 12,
            border: '1px solid var(--separator)',
            background: 'color-mix(in srgb, var(--primary) 5%, var(--background_2))',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <OrderThumb path={selected.thumbnailPath} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: 'var(--font_1)',
                  letterSpacing: '-0.01em',
                  lineHeight: 1.3,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical' as const,
                  overflow: 'hidden',
                  wordBreak: 'break-word',
                }}
              >
                {selected.title}
              </div>
              <ListingMeta listing={selected} />
            </div>
            <button
              type="button"
              onClick={() => onSelect(null)}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--primary)',
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
                padding: '4px 8px',
                flexShrink: 0,
              }}
            >
              Change
            </button>
          </div>
          <OrderIdLine id={selected.id} />
        </div>
      </Field>
    )
  }

  const hasQuery = debouncedQuery.trim().length > 0
  return (
    <Field label="Which listing? (optional)">
      <PickerSearchInput
        value={query}
        onChange={setQuery}
        placeholder="Search by title or listing ID"
      />
      {loading ? (
        <div style={{ fontSize: 13, color: 'var(--font_3)', padding: 8 }}>
          Loading your recent listings…
        </div>
      ) : listings && listings.length > 0 ? (
        <div
          style={{
            border: '1px solid var(--separator)',
            borderRadius: 12,
            overflow: 'hidden',
          }}
        >
          {listings.map((l, i) => (
            <button
              key={l.id}
              type="button"
              onClick={() => onSelect(l)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                padding: '10px 12px',
                background: 'var(--background_2)',
                border: 'none',
                borderTop: i > 0 ? '1px solid var(--separator)' : 'none',
                cursor: 'pointer',
                textAlign: 'left',
                color: 'var(--font_1)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  width: '100%',
                }}
              >
                <OrderThumb path={l.thumbnailPath} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: 'var(--font_1)',
                      letterSpacing: '-0.01em',
                      lineHeight: 1.3,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical' as const,
                      overflow: 'hidden',
                      wordBreak: 'break-word',
                    }}
                  >
                    {l.title}
                  </div>
                  <ListingMeta listing={l} />
                </div>
              </div>
              <OrderIdLine id={l.id} />
            </button>
          ))}
        </div>
      ) : (
        <PickerEmptyState
          message={
            hasQuery
              ? `No listings match "${debouncedQuery.trim()}"`
              : 'No listings yet.'
          }
        />
      )}
    </Field>
  )
}

const ListingMeta = ({ listing }: { listing: HelpListingPickerItem }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      columnGap: 8,
      rowGap: 4,
      marginTop: 4,
    }}
  >
    {listing.status && <ListingStatusBadge status={listing.status} />}
    {listing.price != null && (
      <span
        style={{ fontSize: 12, color: 'var(--font_2)', fontWeight: 500 }}
      >
        {new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: (listing.currency || 'usd').toUpperCase(),
        }).format(listing.price)}
      </span>
    )}
    <span style={{ fontSize: 12, color: 'var(--font_2)' }}>
      Listed {fmtOrderDate(listing.createdAt)}
    </span>
    <span style={{ fontSize: 12, color: 'var(--font_2)' }}>
      {listing.views} {listing.views === 1 ? 'view' : 'views'}
    </span>
    {listing.quantity != null && listing.quantity > 1 && (
      <span style={{ fontSize: 12, color: 'var(--font_2)' }}>
        Qty {listing.quantity}
      </span>
    )}
  </div>
)

const LISTING_STATUS_TONE: Record<string, { color: string; label: string }> = {
  draft: { color: 'var(--font_2)', label: 'Draft' },
  active: { color: 'var(--ios-green)', label: 'Active' },
  scheduled: { color: 'var(--ios-blue)', label: 'Scheduled' },
  sold: { color: 'var(--primary)', label: 'Sold' },
  unsold: { color: 'var(--ios-orange)', label: 'Unsold' },
  cancelled: { color: 'var(--ios-red)', label: 'Cancelled' },
}

const ListingStatusBadge = ({ status }: { status: string }) => {
  const tone = LISTING_STATUS_TONE[status] || {
    color: 'var(--font_2)',
    label: status.charAt(0).toUpperCase() + status.slice(1),
  }
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: 11,
        fontWeight: 600,
        color: tone.color,
        background: `color-mix(in srgb, ${tone.color} 14%, transparent)`,
        padding: '2px 7px',
        borderRadius: 4,
        letterSpacing: '0.02em',
      }}
    >
      {tone.label}
    </span>
  )
}

// ── Payout picker (seller-side) ──────────────────────────────────────────

const PayoutPicker = ({
  selected,
  onSelect,
}: {
  selected: HelpPayoutPickerItem | null
  onSelect: (p: HelpPayoutPickerItem | null) => void
}) => {
  const [payouts, setPayouts] = useState<HelpPayoutPickerItem[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebouncedValue(query, 300)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await ConversationsRepository.getRecentPayouts(
          debouncedQuery || undefined
        )
        if (!cancelled) setPayouts(res)
      } catch {
        if (!cancelled) setPayouts([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [debouncedQuery])

  if (selected) {
    return (
      <Field label="Which payout?">
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            padding: 12,
            borderRadius: 12,
            border: '1px solid var(--separator)',
            background: 'color-mix(in srgb, var(--primary) 5%, var(--background_2))',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <OrderThumb path={selected.thumbnailPath} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: 'var(--font_1)',
                  letterSpacing: '-0.01em',
                  lineHeight: 1.3,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical' as const,
                  overflow: 'hidden',
                  wordBreak: 'break-word',
                }}
              >
                {selected.listingTitle || orderLabel(selected.orderNumber)}
              </div>
              <PayoutMeta payout={selected} />
            </div>
            <button
              type="button"
              onClick={() => onSelect(null)}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--primary)',
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
                padding: '4px 8px',
                flexShrink: 0,
              }}
            >
              Change
            </button>
          </div>
          <OrderNumberLine orderNumber={selected.orderNumber} />
        </div>
      </Field>
    )
  }

  const hasQuery = debouncedQuery.trim().length > 0
  return (
    <Field label="Which payout? (optional)">
      <PickerSearchInput
        value={query}
        onChange={setQuery}
        placeholder="Search by order number, tracking number, or title"
      />
      {loading ? (
        <div style={{ fontSize: 13, color: 'var(--font_3)', padding: 8 }}>
          Loading your recent payouts…
        </div>
      ) : payouts && payouts.length > 0 ? (
        <div
          style={{
            border: '1px solid var(--separator)',
            borderRadius: 12,
            overflow: 'hidden',
          }}
        >
          {payouts.map((p, i) => (
            <button
              key={p.id}
              type="button"
              onClick={() => onSelect(p)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                padding: '10px 12px',
                background: 'var(--background_2)',
                border: 'none',
                borderTop: i > 0 ? '1px solid var(--separator)' : 'none',
                cursor: 'pointer',
                textAlign: 'left',
                color: 'var(--font_1)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  width: '100%',
                }}
              >
                <OrderThumb path={p.thumbnailPath} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: 'var(--font_1)',
                      letterSpacing: '-0.01em',
                      lineHeight: 1.3,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical' as const,
                      overflow: 'hidden',
                      wordBreak: 'break-word',
                    }}
                  >
                    {p.listingTitle || orderLabel(p.orderNumber)}
                  </div>
                  <PayoutMeta payout={p} />
                </div>
              </div>
              <OrderNumberLine orderNumber={p.orderNumber} />
            </button>
          ))}
        </div>
      ) : (
        <PickerEmptyState
          message={
            hasQuery
              ? `No payouts match "${debouncedQuery.trim()}"`
              : 'No paid orders yet.'
          }
        />
      )}
    </Field>
  )
}

const PayoutMeta = ({ payout }: { payout: HelpPayoutPickerItem }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      columnGap: 8,
      rowGap: 4,
      marginTop: 4,
    }}
  >
    <PayoutStatusBadge label={payout.statusLabel} status={payout.paymentStatus} />
    <span
      style={{ fontSize: 12, color: 'var(--font_2)', fontWeight: 600 }}
    >
      {fmtPayoutAmount(payout.sellerAmount)}
    </span>
    <span className="text-xs text-[var(--font_2)] [overflow-wrap:anywhere]">
      {orderLabel(payout.orderNumber)}
    </span>
    {payout.paidAt && (
      <span style={{ fontSize: 12, color: 'var(--font_2)' }}>
        Paid {fmtOrderDate(payout.paidAt)}
      </span>
    )}
  </div>
)

/**
 * Color the status badge by the underlying payment state. Refunds get red,
 * normal in-progress states get blue, settled/paid-out gets green so a
 * glance tells the seller where each order stands.
 */
function payoutBadgeTone(
  status: string,
  label: string
): { color: string; label: string } {
  if (status === 'refunded' || status === 'partially_refunded') {
    return { color: 'var(--ios-red)', label }
  }
  if (label === 'Paid out') {
    return { color: 'var(--ios-green)', label }
  }
  if (label === 'Cancelled') {
    return { color: 'var(--font_2)', label }
  }
  return { color: 'var(--ios-blue)', label }
}

const PayoutStatusBadge = ({
  label,
  status,
}: {
  label: string
  status: string
}) => {
  const tone = payoutBadgeTone(status, label)
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: 11,
        fontWeight: 600,
        color: tone.color,
        background: `color-mix(in srgb, ${tone.color} 14%, transparent)`,
        padding: '2px 7px',
        borderRadius: 4,
        letterSpacing: '0.02em',
      }}
    >
      {tone.label}
    </span>
  )
}

function fmtPayoutAmount(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount)
}

/**
 * Search input rendered above each picker's recent list. 300ms debounce
 * happens in the parent; this component is dumb (controlled input with a
 * leading magnifying-glass icon and a clear button when non-empty).
 */
const PickerSearchInput = ({
  value,
  onChange,
  placeholder,
}: {
  value: string
  onChange: (next: string) => void
  placeholder: string
}) => (
  <div
    style={{
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
    }}
  >
    <Search
      size={15}
      strokeWidth={1.75}
      style={{
        position: 'absolute',
        left: 12,
        color: 'var(--font_3)',
        pointerEvents: 'none',
      }}
    />
    <input
      type="search"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        flex: 1,
        height: 40,
        borderRadius: 10,
        border: '1px solid var(--separator)',
        background: 'var(--background_2)',
        color: 'var(--font_1)',
        fontSize: 14,
        padding: '0 36px 0 34px',
        outline: 'none',
        fontFamily: 'inherit',
      }}
    />
    {value && (
      <button
        type="button"
        onClick={() => onChange('')}
        aria-label="Clear search"
        style={{
          position: 'absolute',
          right: 8,
          background: 'transparent',
          border: 'none',
          color: 'var(--font_3)',
          cursor: 'pointer',
          padding: 4,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 6,
        }}
      >
        <X size={14} strokeWidth={2} />
      </button>
    )}
  </div>
)

const PickerEmptyState = ({ message }: { message: string }) => (
  <div
    style={{
      fontSize: 13,
      color: 'var(--font_3)',
      padding: 12,
      border: '1px dashed var(--separator)',
      borderRadius: 12,
      textAlign: 'center',
    }}
  >
    {message}
  </div>
)

const MAX_TOTAL_FILES = 10

const FileAttacher = ({
  attachments,
  setAttachments,
  totalCount,
}: {
  attachments: MessageAttachment[]
  setAttachments: (next: MessageAttachment[]) => void
  totalCount: number
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const onPick = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return
    const newFiles = Array.from(fileList)
    const remaining = MAX_TOTAL_FILES - totalCount
    if (newFiles.length > remaining) {
      setError(`You can attach up to ${MAX_TOTAL_FILES} files in total`)
      return
    }
    setError(null)
    setUploading(true)
    try {
      const uploaded = await ConversationsRepository.uploadAttachments(newFiles)
      setAttachments([...attachments, ...uploaded])
    } catch (err: any) {
      setError(err.message || 'Upload failed')
    } finally {
      setUploading(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  const remove = (idx: number) => {
    setAttachments(attachments.filter((_, i) => i !== idx))
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,video/*,.pdf,.txt,.rtf,.csv,.json,.md,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.pages,.numbers,.keynote,.zip"
        multiple
        onChange={(e) => onPick(e.target.files)}
        style={{ display: 'none' }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading || totalCount >= MAX_TOTAL_FILES}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            background: 'transparent',
            border: '1px solid var(--separator)',
            color: 'var(--font_1)',
            fontSize: 13,
            padding: '0 12px',
            height: 32,
            borderRadius: 999,
            cursor:
              uploading || totalCount >= MAX_TOTAL_FILES ? 'not-allowed' : 'pointer',
            opacity: uploading || totalCount >= MAX_TOTAL_FILES ? 0.5 : 1,
          }}
        >
          <Paperclip size={13} strokeWidth={1.75} />
          {uploading ? 'Uploading…' : 'Attach a file'}
        </button>
        <span style={{ fontSize: 11, color: 'var(--font_3)' }}>
          {totalCount}/{MAX_TOTAL_FILES} · up to 25 MB each
        </span>
      </div>

      {error && (
        <div style={{ fontSize: 12, color: 'var(--ios-red)' }}>{error}</div>
      )}

      {attachments.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))',
            gap: 8,
          }}
        >
          {attachments.map((a, i) => (
            <AttachmentThumb key={`${a.assetId ?? a.url}-${i}`} a={a} onRemove={() => remove(i)} />
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Upload endpoint returns a relative asset path (e.g. "assets/abc.jpg"). The
 * <img> tag would otherwise resolve that against the frontend origin and 404.
 * Prepend NEXT_PUBLIC_SERVER_URL so it hits the backend's /assets/* route,
 * which redirects to a signed S3 URL (or serves from disk in dev).
 */
function resolveAttachmentUrl(urlOrPath: string): string {
  if (!urlOrPath) return ''
  if (urlOrPath.startsWith('http://') || urlOrPath.startsWith('https://')) {
    return urlOrPath
  }
  const base = process.env.NEXT_PUBLIC_SERVER_URL || ''
  const stripped = urlOrPath.replace(/^\/+/, '')
  return `${base}/${stripped.startsWith('assets/') ? stripped : `assets/${stripped}`}`
}

const AttachmentThumb = ({
  a,
  onRemove,
}: {
  a: MessageAttachment
  onRemove: () => void
}) => {
  const isImage = (a.mime || '').startsWith('image/')
  const isVideo = (a.mime || '').startsWith('video/')

  return (
    <div
      style={{
        position: 'relative',
        aspectRatio: '1 / 1',
        borderRadius: 10,
        overflow: 'hidden',
        background: 'var(--background_3, var(--separator))',
        border: '1px solid var(--separator)',
      }}
    >
      {isImage ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={resolveAttachmentUrl(a.url)}
          alt={a.filename}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
        />
      ) : isVideo ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: 'var(--font_2)',
            gap: 4,
            padding: 6,
          }}
        >
          <Film size={20} strokeWidth={1.5} />
          <span
            style={{
              fontSize: 10,
              textAlign: 'center',
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={a.filename}
          >
            {a.filename}
          </span>
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: 'var(--font_2)',
            gap: 4,
            padding: 6,
          }}
        >
          <FileIcon size={18} />
          <span
            title={a.filename}
            style={{
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: 10,
            }}
          >
            {a.filename}
          </span>
        </div>
      )}

      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${a.filename}`}
        style={{
          position: 'absolute',
          top: 4,
          right: 4,
          width: 20,
          height: 20,
          borderRadius: '50%',
          background: 'rgba(0, 0, 0, 0.65)',
          color: 'white',
          border: 'none',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
        }}
      >
        <X size={12} strokeWidth={2.5} />
      </button>
    </div>
  )
}

const iconButtonStyle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: 'var(--font_2)',
  cursor: 'pointer',
  padding: 6,
  borderRadius: 8,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const selectStyle: React.CSSProperties = {
  width: '100%',
  height: 44,
  borderRadius: 12,
  border: '1px solid var(--separator)',
  background: 'var(--background_2)',
  color: 'var(--font_1)',
  fontSize: 15,
  padding: '0 14px',
  outline: 'none',
  fontFamily: 'inherit',
  cursor: 'pointer',
}

const textareaStyle: React.CSSProperties = {
  width: '100%',
  borderRadius: 12,
  border: '1px solid var(--separator)',
  background: 'var(--background_2)',
  color: 'var(--font_1)',
  fontSize: 15,
  padding: 14,
  outline: 'none',
  fontFamily: 'inherit',
  resize: 'vertical',
  lineHeight: 1.45,
}

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
    <label
      style={{
        fontSize: 13,
        fontWeight: 500,
        color: 'var(--font_1)',
        letterSpacing: '-0.005em',
      }}
    >
      {label}
    </label>
    {children}
  </div>
)

const DebugBundleNote = () => (
  <div
    style={{
      padding: '11px 13px',
      borderRadius: 10,
      background: 'color-mix(in srgb, var(--ios-blue) 6%, transparent)',
      border: '1px solid color-mix(in srgb, var(--ios-blue) 18%, var(--separator))',
      fontSize: 12,
      color: 'var(--font_2)',
      lineHeight: 1.5,
    }}
  >
    Debug info attaches automatically — your browser, recent actions, network
    errors, and the page state we need to reproduce the issue. Sensitive data
    (passwords, payment details, search queries) is never collected.
  </div>
)
